﻿import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import { Dismiss24Regular, Edit20Regular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { MAX_AWARD_TITLE_LENGTH } from '../../../models/schemas/award-request.schema';
import { ValidationMessageList } from '../../shared-components';
import './AddOrEditAwardModal.scss';
import { AddOrEditMasterAwardModal } from './AddOrEditMasterAwardModal';

const CREATE_NEW_MASTER_AWARD_OPTION_VALUE = '__create_new__' as const;

const useStyles = makeStyles({
  masterAwardOption: {
    display: 'flex',
    alignItems: 'center',
  },
  newMasterAwardOption: {
    paddingLeft: '12px',
    color: '#0078D4',
  },
  editIcon: {
    marginLeft: 'auto',
    cursor: 'pointer',
    flexShrink: 0,
  },
});

interface AddOrEditAwardModalProps {
  isModalOpen: boolean;
  onDismissModal(): void;
  onSaveButtonClicked(): void;
}

const AddOrEditAwardModalImpl: React.FC<AddOrEditAwardModalProps> = (props) => {
  const {
    isModalOpen,
    onDismissModal,
    onSaveButtonClicked,
  } = props;

  const {
    manageAwardListPageUiStore,
  } = useStores();

  const styles = useStyles();
  const commonStyles = useCommonStyles();

  const [isBeingValidated, setIsBeingValidated] = useState<boolean>(false);

  const handleDismissModal = (): void => {
    setIsBeingValidated(false);
    onDismissModal();
  };

  const handleSave = (): void => {
    setIsBeingValidated(true);

    if (!manageAwardListPageUiStore.awardRequestValidation.isValid) return;

    onSaveButtonClicked();
    onDismissModal();
  };

  return (
    <>
      <Dialog
        open={isModalOpen}
        onOpenChange={(_, data) => {
          if (!data.open) handleDismissModal();
        }}
      >
        <DialogSurface
          className={mergeClasses(commonStyles.modalSurface, 'add-or-edit-award-modal')}
        >
          <DialogBody
            className={commonStyles.modalBody}
          >
            <DialogTitle
              action={
                <Button
                  appearance='subtle'
                  aria-label='close'
                  icon={<Dismiss24Regular />}
                  onClick={() => handleDismissModal()}
                />
              }
            >
              {
                manageAwardListPageUiStore.addOrEditAwardModalType === 'add' && 'Add Award'
              }
              {
                manageAwardListPageUiStore.addOrEditAwardModalType === 'update' && 'Update Award'
              }
            </DialogTitle>
            <DialogContent>
              {
                manageAwardListPageUiStore.addOrEditAwardModalType === 'add' && (
                  <Field
                    required
                    label={{
                      children: 'Select award from master list',
                      className: commonStyles.inputFieldLabel,
                    }}
                    validationMessage={
                      isBeingValidated
                      && manageAwardListPageUiStore.awardRequestValidation.fieldErrors.masterAwardId?.length
                        ? (
                          <ValidationMessageList
                            messages={manageAwardListPageUiStore.awardRequestValidation.fieldErrors.masterAwardId}
                          />
                        )
                        : undefined
                    }
                    validationMessageIcon={null}
                  >
                    <Dropdown
                      placeholder='Select a master award'
                      selectedOptions={manageAwardListPageUiStore.selectedMasterAwardOptions}
                      value={manageAwardListPageUiStore.selectedMasterAwardValue}
                      className={commonStyles.inputField}
                      onOptionSelect={(_, data) => {
                        if (data.optionValue === CREATE_NEW_MASTER_AWARD_OPTION_VALUE) {
                          manageAwardListPageUiStore.createMasterAwardRequestBody();
                          manageAwardListPageUiStore.setAddOrEditMasterAwardModalType('add');
                          return;
                        }
                        if (!data.optionValue) return;
                        manageAwardListPageUiStore.setAwardRequestBody({ key: 'masterAwardId', value: +data.optionValue });
                      }}
                    >
                      <Option
                        key={CREATE_NEW_MASTER_AWARD_OPTION_VALUE}
                        value={CREATE_NEW_MASTER_AWARD_OPTION_VALUE}
                        text=''
                        checkIcon={null}
                        className={styles.newMasterAwardOption}
                      >
                        + New master award
                      </Option>
                      {
                        manageAwardListPageUiStore.availableMasterAwards.map((masterAward) => (
                          <Option
                            key={masterAward.masterAwardId}
                            value={String(masterAward.masterAwardId)}
                            text={masterAward.title}
                            className={styles.masterAwardOption}
                          >
                            <span>{masterAward.title}</span>
                            <Edit20Regular
                              className={styles.editIcon}
                              onClick={(event) => {
                                event.stopPropagation();
                                manageAwardListPageUiStore.setSelectedMasterAwardId(masterAward.masterAwardId);
                                manageAwardListPageUiStore.createMasterAwardRequestBodyForUpdate(masterAward);
                                manageAwardListPageUiStore.setAddOrEditMasterAwardModalType('update');
                              }}
                            />
                          </Option>
                        ))
                      }
                    </Dropdown>
                  </Field>
                )
              }
              {
                manageAwardListPageUiStore.addOrEditAwardModalType === 'update' && (
                  <Field
                    label={{
                      children: 'Master award',
                      className: commonStyles.inputFieldLabel,
                    }}
                  >
                    <Input
                      className={commonStyles.inputField}
                      value={manageAwardListPageUiStore.selectedMasterAwardTitle}
                      readOnly
                    />
                  </Field>
                )
              }
              <Field
                required
                label={{
                  children: 'Name award for this program',
                  className: commonStyles.inputFieldLabel,
                }}
                validationMessage={
                  isBeingValidated
                  && manageAwardListPageUiStore.awardRequestValidation.fieldErrors.title?.length
                    ? (
                      <ValidationMessageList
                        messages={manageAwardListPageUiStore.awardRequestValidation.fieldErrors.title}
                      />
                    )
                    : undefined
                }
                validationMessageIcon={null}
                hint={`${manageAwardListPageUiStore.awardRequestBody?.title?.length || 0}/${MAX_AWARD_TITLE_LENGTH}`}
              >
                <Input
                  value={manageAwardListPageUiStore.awardRequestBody?.title || ''}
                  className={commonStyles.inputField}
                  maxLength={MAX_AWARD_TITLE_LENGTH}
                  onChange={(_, data) => manageAwardListPageUiStore.setAwardRequestBody({ key: 'title', value: data.value })}
                />
              </Field>
              <Field
                required
                label={{
                  children: 'Select group',
                  className: commonStyles.inputFieldLabel,
                }}
                validationMessage={
                  isBeingValidated
                  && manageAwardListPageUiStore.awardRequestValidation.fieldErrors.groupId?.length
                    ? (
                      <ValidationMessageList
                        messages={manageAwardListPageUiStore.awardRequestValidation.fieldErrors.groupId}
                      />
                    )
                    : undefined
                }
                validationMessageIcon={null}
              >
                <Dropdown
                  placeholder='Select a group'
                  className={commonStyles.inputField}
                  selectedOptions={manageAwardListPageUiStore.selectedAwardGroupOptions}
                  value={manageAwardListPageUiStore.selectedAwardGroupValue}
                  onOptionSelect={(_, data) => {
                    if (!data.optionValue) return;
                    manageAwardListPageUiStore.setAwardRequestBody({ key: 'groupId', value: +data.optionValue });
                  }}
                >
                  {
                    manageAwardListPageUiStore.availableAwardGroups.map((group) => (
                      <Option
                        key={group.awardGroupId}
                        value={String(group.awardGroupId)}
                      >
                        {group.title}
                      </Option>
                    ))
                  }
                </Dropdown>
                {
                  manageAwardListPageUiStore.selectedAward?.isRegionAward && (
                    <Checkbox
                      checked
                      readOnly
                      disabled
                      label='This award is a region award'
                    />
                  )
                }
              </Field>
            </DialogContent>
            <DialogActions>
              <Button
                appearance='secondary'
                onClick={() => handleDismissModal()}
              >
                Back
              </Button>
              <Button
                appearance='primary'
                disabled={isBeingValidated && !manageAwardListPageUiStore.awardRequestValidation.isValid}
                onClick={() => handleSave()}
              >
                {
                  manageAwardListPageUiStore.addOrEditAwardModalType === 'add' && 'Add'
                }
                {
                  manageAwardListPageUiStore.addOrEditAwardModalType === 'update' && 'Update'
                }
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {
        manageAwardListPageUiStore.addOrEditMasterAwardModalType !== undefined && (
          <AddOrEditMasterAwardModal
            isModalOpen={manageAwardListPageUiStore.addOrEditMasterAwardModalType !== undefined}
            onDismissModal={() => manageAwardListPageUiStore.setAddOrEditMasterAwardModalType(undefined)}
          />
        )
      }
    </>
  );
};

export const AddOrEditAwardModal = observer(AddOrEditAwardModalImpl);
